/* ---------------------------------------------------------------------------
   Swipeable photos on product tiles.

   The markup is a scroll-snap track rendered by snippets/product-item.liquid:
   one slide per image the product has, so a shopper can flick through every
   photo without opening the product. The browser owns the swipe, the momentum
   and the snap; this file only does the four things CSS cannot:

     - open the track on the featured photo when it is not the first slide
     - scroll to a colour's photo when its swatch is clicked
     - keep the dots / counter / arrows in step with the slide on screen
     - give a mouse a way in: hover arrows, and drag to scroll

   The swatch handler in custom.js still rewrites the tile's link and price on
   a swatch click; it just no longer has any image to swap, because the slides
   do not wear the class it hides. This file reads the same click after it.
   --------------------------------------------------------------------------- */
(function () {
    'use strict';

    var TRACK = '[data-tile-media]';
    var SLIDE = '.lux-tile-media__slide';

    function slidesOf(track) {
        return track.querySelectorAll(SLIDE);
    }

    function width(track) {
        return track.clientWidth || 1;
    }

    /* Nearest slide to the scroll position, by the slides' own offsets. Not
       scrollLeft / clientWidth: clientWidth is an integer and the tile is
       not, so by the tenth slide the division is a third of a slide out. */
    function nearest(track) {
        var slides = slidesOf(track);
        var left = track.scrollLeft;
        var best = 0;
        var gap = Infinity;
        for (var i = 0; i < slides.length; i++) {
            var d = Math.abs(slides[i].offsetLeft - left);
            if (d < gap) { gap = d; best = i; }
        }
        return { index: best, gap: gap };
    }

    function indexOf(track) {
        return nearest(track).index;
    }

    var flights = new WeakMap();

    function goTo(track, index, smooth) {
        var slides = slidesOf(track);
        if (!slides.length) return;
        index = Math.max(0, Math.min(slides.length - 1, index));
        track.dataset.tileIndex = String(index);
        /* The slide's own offset rather than index * width: on a phone the
           tile is 151.5px wide and clientWidth says 152, and four of those
           rounding errors is a landing two pixels off the snap point. */
        var left = slides[index].offsetLeft;

        if (flights.has(track)) cancelAnimationFrame(flights.get(track));
        flights.delete(track);

        if (!smooth) {
            track.classList.remove('is-jumping');
            track.scrollTo({ left: left, behavior: 'auto' });
            return;
        }

        /* Not scrollTo({behavior: 'smooth'}). scroll-snap-stop: always lets
           a scroll pass one snap point at a time, and Chrome holds a smooth
           programmatic scroll to the same rule, so a swatch three colours
           away landed one slide over. The flight is driven here instead -
           280ms, ease-out - with snap off for the duration (is-jumping) and
           back on at the landing, which is exactly a snap point anyway. */
        var from = track.scrollLeft;
        var distance = left - from;
        if (Math.abs(distance) < 1) return;
        var started = null;
        var duration = 280;
        track.classList.add('is-jumping');
        var step = function (now) {
            if (started === null) started = now;
            var t = Math.min(1, (now - started) / duration);
            var eased = 1 - (1 - t) * (1 - t) * (1 - t);
            track.scrollLeft = from + distance * eased;
            if (t < 1) {
                flights.set(track, requestAnimationFrame(step));
            } else {
                flights.delete(track);
                track.classList.remove('is-jumping');
            }
        };
        flights.set(track, requestAnimationFrame(step));
    }

    /* Everything that reflects position - the active slide, the dot, the
       counter, whether an arrow has anywhere to go. One function, called
       from the scroll event, so the dots follow a finger swipe as well as a
       swatch click. */
    function paint(track) {
        var index = indexOf(track);
        var slides = slidesOf(track);
        slides.forEach(function (slide, i) { slide.classList.toggle('is-active', i === index); });

        var wrapper = track.closest('.product-item__image-wrapper');
        if (!wrapper) return;

        /* Dots. Up to WINDOW of them the strip is static and centred. Past
           that it becomes a window that slides to keep the active dot near
           the middle, and the dot at an end tapers while there are more
           beyond it, which is what implies the rest of the set. */
        var WINDOW = 7;
        var dotsEl = wrapper.querySelector('[data-tile-dots]');
        var dots = wrapper.querySelectorAll('.lux-tile-media__dot');
        var offset = 0;
        if (dots.length > WINDOW) {
            offset = Math.min(Math.max(index - Math.floor(WINDOW / 2), 0), dots.length - WINDOW);
        }
        var windowed = dots.length > WINDOW;
        var moreBefore = offset > 0;
        var moreAfter = offset + WINDOW < dots.length;
        dots.forEach(function (dot, i) {
            dot.classList.toggle('is-active', i === index);
            var seat = i - offset;
            /* Out-of-window dots leave the flow entirely. Nothing is clipped,
               so the dots render exactly as they do on a short strip. */
            dot.classList.toggle('is-out', windowed && (seat < 0 || seat >= WINDOW));
            dot.classList.toggle('is-edge', windowed && ((seat === 0 && moreBefore) || (seat === WINDOW - 1 && moreAfter)));
            dot.classList.toggle('is-edge-near', windowed && ((seat === 1 && moreBefore) || (seat === WINDOW - 2 && moreAfter)));
        });
        if (dotsEl) dotsEl.hidden = dots.length < 2;
        var prev = wrapper.querySelector('[data-tile-prev]');
        var next = wrapper.querySelector('[data-tile-next]');
        if (prev) prev.disabled = index === 0;
        if (next) next.disabled = index >= slides.length - 1;
    }

    /* Slide widths are the track width, so a resize - a phone rotating, a
       drawer opening beside the grid - leaves scrollLeft pointing between two
       slides. Re-snap to the index we were on rather than trusting the pixel. */
    var resizer = window.ResizeObserver
        ? new ResizeObserver(function (entries) {
            entries.forEach(function (entry) {
                var track = entry.target;
                if (track.classList.contains('is-jumping')) return;
                var index = Number(track.dataset.tileIndex || 0);
                if (indexOf(track) !== index) goTo(track, index, false);
            });
        })
        : null;

    function init(track) {
        if (track.dataset.tileReady) return;
        track.dataset.tileReady = '1';

        var start = Number(track.dataset.initialIndex || 0);
        if (start > 0 && width(track) > 1) goTo(track, start, false);
        else track.dataset.tileIndex = String(start);
        paint(track);

        var frame = null;
        track.addEventListener('scroll', function () {
            if (frame) return;
            frame = requestAnimationFrame(function () {
                frame = null;
                paint(track);
                /* A finger swipe moves the index without going through goTo,
                   so record where it landed for the resize handler. Only once
                   the scroll has settled on a slide, or a mid-swipe read would
                   be stored as the destination. A goTo flight is skipped
                   too: it has already stored where it is going. */
                if (track.classList.contains('is-jumping')) return;
                var at = nearest(track);
                if (at.gap < 2) track.dataset.tileIndex = String(at.index);
            });
        }, { passive: true });

        if (resizer) resizer.observe(track);
    }

    function initAll(root) {
        (root || document).querySelectorAll(TRACK).forEach(init);
    }

    /* ---- swatches --------------------------------------------------------- */

    /* Same click custom.js handles. It runs first (theme.js, custom.js and
       this file are all deferred, in that order) and decides whether the
       click selects the colour or, on a second click, follows the link. This
       only needs the media id; on the navigating click the scroll is
       harmless. Pills carry no media id - a size does not change the photo. */
    document.addEventListener('click', function (e) {
        var swatch = e.target.closest ? e.target.closest('a[data-card-swatch]') : null;
        if (!swatch || !swatch.dataset.mediaId) return;
        var item = swatch.closest('product-item');
        var track = item && item.querySelector(TRACK);
        if (!track) return;
        var slide = track.querySelector(SLIDE + '[data-media-id="' + swatch.dataset.mediaId + '"]');
        if (slide) goTo(track, Number(slide.dataset.index), true);
    });

    /* ---- arrows ----------------------------------------------------------- */

    document.addEventListener('click', function (e) {
        var arrow = e.target.closest ? e.target.closest('[data-tile-prev], [data-tile-next]') : null;
        if (!arrow) return;
        e.preventDefault();
        var wrapper = arrow.closest('.product-item__image-wrapper');
        var track = wrapper && wrapper.querySelector(TRACK);
        if (!track) return;
        var step = arrow.hasAttribute('data-tile-prev') ? -1 : 1;
        goTo(track, Number(track.dataset.tileIndex || indexOf(track)) + step, true);
    });

    /* ---- mouse drag ------------------------------------------------------- */

    /* A finger has native scrolling; a mouse does not, and a slider you can
       only move with 30px hover arrows is a slider most people never move.
       Pointer events rather than mouse events so a pen behaves the same, but
       touch is excluded: the browser is already scrolling for it.

       Snap is turned off for the duration (the is-dragging class) because
       scroll-snap fights a scrollLeft that is being set every frame. It is
       decided afterwards by how far the pointer travelled: more than 15% of
       a slide and the drag counts as a swipe to the neighbour, less and it
       springs back. And the click that follows a drag is swallowed, or every
       drag would also open the product. */
    var drag = null;

    document.addEventListener('pointerdown', function (e) {
        var track = e.target.closest ? e.target.closest(TRACK) : null;
        if (!track) return;
        /* A finger or button landing mid-flight takes over: the flight is
           cancelled where it is and snap is handed straight back. */
        if (flights.has(track)) {
            cancelAnimationFrame(flights.get(track));
            flights.delete(track);
            track.classList.remove('is-jumping');
        }
        if (e.pointerType === 'touch' || e.button !== 0) return;
        drag = { track: track, x: e.clientX, left: track.scrollLeft, from: indexOf(track), moved: false };
    });

    document.addEventListener('pointermove', function (e) {
        if (!drag) return;
        var dx = e.clientX - drag.x;
        if (!drag.moved) {
            if (Math.abs(dx) < 4) return;
            drag.moved = true;
            drag.track.classList.add('is-dragging');
        }
        drag.track.scrollLeft = drag.left - dx;
        e.preventDefault();
    }, { passive: false });

    function endDrag(e) {
        if (!drag) return;
        var done = drag;
        drag = null;
        if (!done.moved) return;
        done.track.classList.remove('is-dragging');
        var dx = e.clientX - done.x;
        var threshold = width(done.track) * 0.15;
        var to = done.from;
        if (dx < -threshold) to += 1;
        else if (dx > threshold) to -= 1;
        goTo(done.track, to, true);

        var swallow = function (ev) {
            ev.preventDefault();
            ev.stopPropagation();
            document.removeEventListener('click', swallow, true);
        };
        document.addEventListener('click', swallow, true);
        setTimeout(function () { document.removeEventListener('click', swallow, true); }, 60);
    }

    document.addEventListener('pointerup', endDrag);
    document.addEventListener('pointercancel', endDrag);

    /* Images and links are both draggable by default, and a track full of
       linked images would start a browser drag-and-drop on the first pull. */
    document.addEventListener('dragstart', function (e) {
        if (e.target.closest && e.target.closest(TRACK)) e.preventDefault();
    });

    /* ---- new tiles -------------------------------------------------------- */

    /* Facets re-render the grid, Load more appends to it, and quick view and
       the cart drawer bring their own tiles. Watching childList is what
       catches all of them; the debounce is because a re-render arrives as
       hundreds of mutations in one frame. */
    (function () {
        if (!window.MutationObserver) return;
        var pending = null;
        var observer = new MutationObserver(function () {
            if (pending) return;
            pending = setTimeout(function () {
                pending = null;
                initAll();
            }, 100);
        });
        var start = function () {
            observer.observe(document.body, { childList: true, subtree: true });
            initAll();
        };
        if (document.body) start();
        else document.addEventListener('DOMContentLoaded', start);
    })();

    /* Tiles inside a closed drawer or a hidden tab measure 0 wide on load,
       so their opening slide could not be scrolled to. Run once more once
       everything has laid out, and again whenever the theme reports a page
       transition finishing. */
    window.addEventListener('load', function () {
        document.querySelectorAll(TRACK).forEach(function (track) {
            var index = Number(track.dataset.tileIndex || 0);
            if (index > 0 && indexOf(track) !== index) goTo(track, index, false);
        });
    });
    document.addEventListener('theme:loading:end', function () { setTimeout(initAll, 200); });
})();
